import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreativePreviewStatus,
  useMetaCreativePreviews,
  useRefreshMetaCreativePreviews,
  useReviewMetaCreativePreview,
} from '@/api/manage-hooks';
import type { CreativeReviewFeedback, MetaPreviewPlacement } from '@/api/types.gen';
import { FailureNotice } from '@/components/failure-notice';
import { frameBox, frameScale, usePreviewWidth } from '@/components/meta-preview-frame';

/// How many tiles to show while the queue job is still writing rows. Matches
/// MetaPreviewPlacement::ALL; being wrong only changes how many skeletons appear
/// for a few seconds, never what is gated.
const EXPECTED_PLACEMENTS = 10;


export interface MetaPreviewGalleryProps {
  creativeId: string;
  onReviewChange?: (complete: boolean) => void;
  readOnly?: boolean;
  /** Regenerate stale previews on mount. Only the review/approve surfaces should
   *  opt in: refreshing enqueues a provider sync and writes an audit row, so a
   *  read-only surface doing it would attribute a write to someone who only
   *  opened a drawer. */
  autoRefresh?: boolean;
}

export function MetaPreviewGallery({
  creativeId,
  onReviewChange,
  readOnly = false,
  autoRefresh = false,
}: MetaPreviewGalleryProps) {
  const { frameRef, previewWidth } = usePreviewWidth();
  const query = useMetaCreativePreviews(creativeId, !!creativeId);
  const refresh = useRefreshMetaCreativePreviews();
  const review = useReviewMetaCreativePreview();
  // Preview rows only appear once the queue job reaches generation. If it dies
  // earlier — missing provider credentials, an asset that will not upload — the
  // bundle stays empty forever and the panel would spin indefinitely while
  // sync_state has recorded the reason all along. Poll it only while there is
  // nothing rendered; a healthy creative stops asking.
  const syncStatus = useCreativePreviewStatus(creativeId, {
    enabled: !!creativeId && !query.data?.rendered,
  });
  const [decisions, setDecisions] = useState<Record<string, 'reviewed' | 'changes_requested'>>({});
  const refreshRequestedFor = useRef<string | null>(null);
  const variants = useMemo(() => query.data?.variants ?? [], [query.data]);
  // Gates on `rendered`, matching the backend. `fresh` only says whether the
  // cached iframe URLs are worth re-minting in the background.
  const complete = !!query.data?.rendered
    && variants.length > 0
    && variants.every((variant) => decisions[variant.placement] === 'reviewed');

  // Hold the callback in a ref so its identity never feeds the effect below.
  // Callers routinely pass an inline arrow; depending on it directly turns every
  // notification into a parent re-render into a fresh identity into another
  // notification — an unbounded loop that freezes the tab in production.
  const notify = useRef(onReviewChange);
  notify.current = onReviewChange;
  useEffect(() => {
    notify.current?.(complete);
  }, [complete]);

  useEffect(() => {
    setDecisions(Object.fromEntries(
      (query.data?.reviewed_placements ?? []).map((placement) => [placement, 'reviewed']),
    ));
  }, [query.data?.content_hash, query.data?.reviewed_placements]);

  // Request generation as soon as we know none is current — do not wait for the
  // operator to find the Refresh button. Reaching this step *is* the intent to
  // preview. The request returns 202 and the queue does the work, so there is a
  // real gap before any row exists; `requested` exists so the copy below can say
  // "requesting" rather than telling someone to press a button that has already
  // been pressed for them.
  const [requested, setRequested] = useState(false);
  useEffect(() => {
    const bundle = query.data;
    if (
      autoRefresh
      && bundle
      && !bundle.fresh
      && refreshRequestedFor.current !== bundle.content_hash
      && !refresh.isPending
    ) {
      refreshRequestedFor.current = bundle.content_hash;
      setRequested(true);
      void refresh.mutateAsync(creativeId).catch(() => {
        // Surfaced through refresh.error below; the ref keeps this to one attempt.
      });
    }
  }, [autoRefresh, creativeId, query.data, refresh]);

  const stale = useMemo(
    () => variants.some((variant) =>
      variant.refresh_after && Date.parse(variant.refresh_after) <= Date.now()),
    [variants],
  );

  const decide = useCallback(async (
    placement: MetaPreviewPlacement,
    decision: 'reviewed' | 'changes_requested',
  ) => {
    // Optimism here is unsafe: the backend re-checks preview freshness and 409s
    // on a stale bundle. Only record the decision the server actually accepted.
    await review.mutateAsync({ creativeId, placement, decision });
    setDecisions((current) => ({ ...current, [placement]: decision }));
  }, [creativeId, review]);

  if (query.isPending) {
    return (
      <div className="meta-preview-loading">
        <span className="btn-spinner" aria-hidden="true" style={{marginRight:8}} />
        Preparing official Meta previews…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="asset-err">
        <span>⚠</span>
        <span>{query.error?.message || 'Official Meta previews could not be loaded.'}</span>
        <button className="btn btn-sm" onClick={() => void query.refetch()}>Retry</button>
      </div>
    );
  }

  const total = variants.length;
  const readyCount = variants.filter((variant) => variant.status === 'ready').length;
  // Before the queue job has written any rows there is nothing to render, which
  // used to leave a completely blank panel. Show the placement slots we know are
  // coming so the operator can see the work is scoped and under way.
  const pending = total === 0 && syncStatus.data?.meta_status !== 'failed'
    ? Array.from({ length: EXPECTED_PLACEMENTS }, (_, index) => index)
    : [];
  const reviewedCount = variants.filter(
    (variant) => decisions[variant.placement] === 'reviewed',
  ).length;
  const blocked = (query.data?.internal_feedback ?? []).filter(
    (feedback) => feedback.decision === 'changes_requested',
  );
  // A failed sync is why nothing is arriving. Say so instead of spinning.
  const generationFailed = syncStatus.data?.meta_status === 'failed'
    ? (syncStatus.data.last_error ?? 'unknown')
    : null;

  return (
    <div className="meta-preview-gallery">
      <div className="meta-preview-gallery-head">
        <div>
          <div className="field-lbl">Official Meta placement previews</div>
          <div className="muted" style={{fontSize:11}}>
            Server-generated Meta iframes · expire after 24 hours
          </div>
        </div>
        <button
          className={`btn btn-sm${refresh.isPending ? ' btn-busy' : ''}`}
          disabled={refresh.isPending}
          onClick={() => void refresh.mutateAsync(creativeId).catch(() => {})}
        >
          {refresh.isPending
            ? <><span className="btn-spinner" aria-hidden="true" />Requesting…</>
            : stale ? 'Re-render previews' : 'Refresh previews'}
        </button>
      </div>
      {review.isError && (
        <div className="asset-err" style={{marginBottom:10}}>
          <span>⚠</span>
          <span>{review.error?.message || 'That review decision could not be saved.'}</span>
        </div>
      )}
      {refresh.isError && (
        <div className="asset-err" style={{marginBottom:10}}>
          <span>⚠</span>
          <span>{refresh.error?.message || 'Previews could not be regenerated.'}</span>
        </div>
      )}
      {!query.data?.rendered && generationFailed && (
        <div className="asset-err" style={{marginBottom:10}}>
          <span>⚠</span>
          {syncStatus.data?.failure ? (
            // The server's words for the same failure the library shows.
            <span style={{minWidth:0}}>
              <span>Meta preview generation failed{syncStatus.data.attempts ? ` after ${syncStatus.data.attempts} attempts` : ''}. Use Refresh previews to try again.</span>
              <FailureNotice dense announce={false} failure={syncStatus.data.failure} />
            </span>
          ) : (
            <span>
              Meta preview generation failed ({generationFailed.replaceAll('_', ' ')})
              {syncStatus.data?.attempts ? ` after ${syncStatus.data.attempts} attempts` : ''}.
              {generationFailed === 'configuration_error'
                ? ' This environment is missing provider configuration — an administrator must complete setup.'
                : ' Use Refresh previews to try again.'}
            </span>
          )}
        </div>
      )}
      {!query.data?.rendered && !generationFailed && (
        <div className="meta-preview-banner">
          <span className="btn-spinner" aria-hidden="true" style={{marginRight:8}} />
          {total === 0
            ? (requested || refresh.isPending
              ? 'Requesting official previews from Meta — this takes a few moments while your assets are sent across.'
              : 'No official previews yet for this creative revision.')
            : `Meta is rendering ${readyCount} of ${total} placements…`}
        </div>
      )}
      {query.data?.rendered && !query.data.fresh && (
        <div className="meta-preview-banner">
          <span>
            These previews were rendered a while ago and Meta signs them for 24 hours.
            {autoRefresh ? ' A fresh copy is being requested in the background.' : ' Refresh to see any that expired.'}
          </span>
          {!autoRefresh && (
            <button
              type="button"
              className={`btn btn-sm${refresh.isPending ? ' btn-busy' : ''}`}
              disabled={refresh.isPending}
              onClick={() => void refresh.mutateAsync(creativeId).catch(() => {})}
            >
              {refresh.isPending ? 'Requesting…' : 'Refresh'}
            </button>
          )}
        </div>
      )}
      {blocked.length > 0 && (
        <div className="asset-err" style={{marginBottom:10}}>
          <span>⚠</span>
          <span>
            Changes requested on {blocked.map((feedback) => feedback.placement).join(', ')}.
            Approval stays blocked until each is re-reviewed.
          </span>
        </div>
      )}
      <div className="meta-preview-grid">
        {variants.map((variant) => {
          const decision = decisions[variant.placement];
          const feedback = [
            ...(query.data?.internal_feedback ?? []),
            ...(query.data?.external_feedback ?? []),
          ].filter((entry) => entry.placement === variant.placement);
          // Meta signs each embed for 24 hours; past `expires_at` the iframe would
          // only show Meta's own "Preview Expired" error, so it becomes our state.
          const expired = variant.status === 'ready'
            && !!variant.expires_at
            && Date.parse(variant.expires_at) <= Date.now();
          return (
            <article className="meta-preview-card" key={variant.placement}>
              <div className="meta-preview-card-head">
                <div>
                  <b>{variant.label}</b>
                  <span>{variant.ad_format}</span>
                </div>
                <span className={`tag ${variant.status !== 'ready' || expired ? 'warn' : ''}`}>
                  {expired ? 'expired' : variant.status}
                </span>
              </div>
              <div className="meta-preview-frame" ref={frameRef} style={frameBox(variant, previewWidth)}>
                {expired ? (
                  <div className="meta-preview-placeholder meta-preview-expired">
                    <svg width="28" height="28" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="10" cy="10" r="7"/><path d="M10 6v4l3 2"/></svg>
                    <strong>Preview expired</strong>
                    <span>Meta signs each preview for 24 hours. Request a fresh render to see this placement again.</span>
                    <button
                      type="button"
                      className={`btn${refresh.isPending ? ' btn-busy' : ''}`}
                      disabled={refresh.isPending}
                      onClick={() => void refresh.mutateAsync(creativeId).catch(() => {})}
                    >
                      {refresh.isPending ? 'Requesting…' : 'Refresh previews'}
                    </button>
                  </div>
                ) : variant.status === 'ready' && variant.embed_url ? (
                  <iframe
                    src={variant.embed_url}
                    title={`${variant.label} Meta preview`}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    allow="autoplay; encrypted-media"
                    style={frameScale(variant, previewWidth)}
                  />
                ) : (
                  <div className="meta-preview-placeholder">
                    {variant.error_code || 'Preview is being generated…'}
                  </div>
                )}
              </div>
              {feedback.length > 0 && (
                <div style={{padding:'8px 10px',borderTop:'1px solid var(--line-2)'}}>
                  {feedback.map((entry) => (
                    <FeedbackLine key={`${entry.reviewer_name}-${entry.decided_at}`} entry={entry} />
                  ))}
                </div>
              )}
              {!readOnly && (
                <div className="meta-preview-actions">
                  <button
                    className={`btn btn-sm ${decision === 'reviewed' ? 'btn-primary' : ''}`}
                    disabled={variant.status !== 'ready' || review.isPending}
                    onClick={() => { void decide(variant.placement, 'reviewed').catch(() => {}); }}
                  >
                    ✓ Reviewed
                  </button>
                  <button
                    className={`btn btn-sm ${decision === 'changes_requested' ? 'btn-danger' : ''}`}
                    disabled={variant.status !== 'ready' || review.isPending}
                    onClick={() => { void decide(variant.placement, 'changes_requested').catch(() => {}); }}
                  >
                    Request changes
                  </button>
                </div>
              )}
            </article>
          );
        })}
        {pending.map((index) => (
          <article className="meta-preview-card is-pending" key={`pending-${index}`}>
            <div className="meta-preview-card-head">
              <div><b>Placement {index + 1}</b><span>waiting for Meta</span></div>
              <span className="tag warn">queued</span>
            </div>
            <div className="meta-preview-frame">
              <div className="meta-preview-placeholder">
                <span className="btn-spinner" aria-hidden="true" />
              </div>
            </div>
          </article>
        ))}
      </div>
      {!readOnly && (
        <div className="meta-preview-review-state">
          {complete
            ? `✓ All ${total} official Meta placements reviewed`
            : `${reviewedCount} of ${total || '…'} placements reviewed`}
        </div>
      )}
    </div>
  );
}

function FeedbackLine({ entry }: { entry: CreativeReviewFeedback }) {
  const rejected = entry.decision === 'changes_requested';
  return (
    <div className="muted" style={{fontSize:10.5,marginBottom:4}}>
      <b style={{color: rejected ? 'var(--bad)' : 'var(--good)'}}>
        {rejected ? 'Changes requested' : 'Reviewed'}
      </b>
      {' · '}{entry.reviewer_name}
      {entry.comment ? ` — ${entry.comment}` : ''}
    </div>
  );
}
